import { Navigate, useParams } from 'react-router-dom'
import { useState } from 'react'
import {
  InvestigationSpine,
  type InvestigationTab,
} from '@/components/layouts/InvestigationSpine'
import { ActorContextStrip } from '@/components/detail/ActorContextStrip'
import { DetailActionBar } from '@/components/detail/DetailActionBar'
import {
  ExploreTab,
  type ExtraSection,
  type Reference,
  type SourceSummary,
} from '@/components/detail/ExploreTab'
import { TimelineTab, type TimelineEvent } from '@/components/detail/TimelineTab'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { useThreatActor, useArticles } from '@/hooks/queries'
import { formatDate } from '@/lib/utils'
import { ApiError } from '@/lib/api'
import { normaliseReferences } from '@/lib/references'
import { campaignsSurfaceEnabled } from '@/lib/campaigns-surface'
import { AttributionPanel } from '@/components/campaigns/AttributionPanel'
import { TypeStatusDetail } from '@/components/campaigns/TypeStatus'
import { CAMPAIGN_COPY } from '@/components/campaigns/copy'
import {
  RecentClaimsTab,
  useActorHasLeakSiteActivity,
} from '@/components/leak-sites/RecentClaimsTab'

export function ThreatActorDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: actor, isLoading, isError, error, refetch } = useThreatActor(id)
  const { data: relatedArticles } = useArticles(
    actor ? { search: actor.name } : undefined,
  )
  const [watched, setWatched] = useState(false)
  // Plan B Plan 2 T15: Recent Claims tab is hidden entirely when no
  // ransomware_groups row links to this actor (spec §5.3 visibility rule).
  const hasLeakSiteActivity = useActorHasLeakSiteActivity(id)

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (isError && !(error instanceof ApiError && error.status === 404)) {
    return (
      <div className="p-6" role="alert">
        <p className="text-muted-foreground">Unable to load this threat actor.</p>
        <button
          type="button"
          className="mt-3 text-sm font-medium text-primary hover:underline"
          onClick={() => refetch()}
        >
          Retry
        </button>
      </div>
    )
  }

  if (!actor) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Threat actor not found.</p>
      </div>
    )
  }

  // KAN-736 R22 (KAN-830 PR 10) — a campaign-typed id: the API's 307 was
  // followed to the campaign JSON, so replace the URL with the campaign route.
  // Codex r1 on PR 1393: only with the campaign surface flag on; off, there is
  // no campaign route, and a campaign is not an actor.
  if (actor.recordType === 'campaign') {
    if (!campaignsSurfaceEnabled()) {
      return (
        <div className="p-6">
          <p className="text-muted-foreground">Threat actor not found.</p>
        </div>
      )
    }
    return <Navigate to={`/campaigns/${actor.id}`} replace />
  }

  // Defensive parse for knownTtps (unknown — could be string[] or object-of-strings)
  const ttps: string[] = Array.isArray(actor.knownTtps)
    ? (actor.knownTtps as string[])
    : actor.knownTtps && typeof actor.knownTtps === 'object'
      ? Object.values(actor.knownTtps as Record<string, string>)
      : []

  // References: heterogeneous shapes (string[], object-of-strings, structured objects) — normalise to Reference[]
  const exploreReferences: Reference[] = normaliseReferences(actor.references)
  const sources: SourceSummary[] = (relatedArticles ?? [])
    .slice(0, 5)
    .map((a) => ({ id: a.id, title: a.publisher }))

  const extraSections: ExtraSection[] = []

  // Profile sources panel (MITRE / MISP / Malpedia external links)
  const hasAnySource = !!(actor.mitreId || actor.mispUuid || actor.malpediaId)
  if (hasAnySource) {
    extraSections.push({
      id: 'profile-sources',
      title: 'Profile sources',
      content: (
        <section aria-label="Profile sources">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {actor.mitreId && (
              <a
                href={`https://attack.mitre.org/groups/${actor.mitreId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col gap-1 rounded-md border bg-card p-3 hover:border-primary"
              >
                <span className="text-xs font-semibold uppercase">MITRE</span>
                <span className="text-sm">{actor.mitreId}</span>
              </a>
            )}
            {actor.mispUuid && (
              <a
                href="https://www.misp-project.org/galaxy.html#_threat_actor"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col gap-1 rounded-md border bg-card p-3 hover:border-primary"
              >
                <span className="text-xs font-semibold uppercase">MISP</span>
                <span className="text-sm font-mono">{actor.mispUuid.slice(0, 8)}…</span>
              </a>
            )}
            {actor.malpediaId && (
              <a
                href={`https://malpedia.caad.fkie.fraunhofer.de/actor/${actor.malpediaId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col gap-1 rounded-md border bg-card p-3 hover:border-primary"
              >
                <span className="text-xs font-semibold uppercase">Malpedia</span>
                <span className="text-sm">{actor.malpediaId}</span>
              </a>
            )}
          </div>
          {actor.sourcesLastSyncedAt && (
            <p className="text-xs text-muted-foreground mt-3">
              Last synced {new Date(actor.sourcesLastSyncedAt).toLocaleDateString()}
            </p>
          )}
        </section>
      ),
    })
  }

  // KAN-736 R23/R25 (KAN-830 PR 10) — behind the campaign-surface flag, so
  // nothing visible changes before Gate A.
  if (campaignsSurfaceEnabled()) {
    extraSections.push({
      id: 'record-type',
      title: 'Record type',
      content: <TypeStatusDetail typeStatus={actor.typeStatus} authorities={actor.typeAuthorities} />,
    })
    if ((actor.attributedCampaigns ?? []).length > 0 || (actor.unrenderedAttributions?.total ?? 0) > 0) {
      extraSections.push({
        id: 'attributed-campaigns',
        title: CAMPAIGN_COPY.campaignsAttributedToGroup,
        content: (
          <AttributionPanel
            title={CAMPAIGN_COPY.campaignsAttributedToGroup}
            groups={(actor.attributedCampaigns ?? []).map((c) => ({
              id: c.campaignId,
              name: c.name,
              href: `/campaigns/${c.campaignId}`,
              typeStatus: c.typeStatus,
              assertions: c.assertions,
            }))}
            unrendered={actor.unrenderedAttributions}
          />
        ),
      })
    }
  }

  if (ttps.length > 0) {
    extraSections.push({
      id: 'ttps',
      title: 'Known TTPs',
      content: (
        <div className="flex flex-wrap gap-2">
          {ttps.map((t, i) => (
            <Badge key={i} variant="outline">
              {String(t)}
            </Badge>
          ))}
        </div>
      ),
    })
  }
  if (actor.targetRegions && actor.targetRegions.length > 0) {
    extraSections.push({
      id: 'target-regions',
      title: 'Target Regions',
      content: (
        <div className="flex flex-wrap gap-1.5">
          {actor.targetRegions.map((r) => (
            <Badge key={r} variant="secondary">
              {r}
            </Badge>
          ))}
        </div>
      ),
    })
  }
  if (relatedArticles && relatedArticles.length > 0) {
    extraSections.push({
      id: 'related-articles',
      title: 'Related Articles',
      content: (
        <div className="space-y-2">
          {relatedArticles.slice(0, 5).map((a) => (
            <div key={a.id} className="rounded-md border bg-card p-3">
              <p className="text-sm font-medium">{a.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {a.publisher} · {formatDate(a.publishedAt)}
              </p>
            </div>
          ))}
        </div>
      ),
    })
  }

  const events: TimelineEvent[] = []
  ;(relatedArticles ?? []).forEach((a) => {
    events.push({
      id: `article-${a.id}`,
      at: a.publishedAt,
      kind: 'mention',
      label: `${a.publisher}: ${a.title}`,
    })
  })

  const tabs: InvestigationTab[] = [
    {
      id: 'explore',
      label: 'Explore',
      content: (
        <ExploreTab
          summary={actor.description ?? undefined}
          extraSections={extraSections}
          references={exploreReferences}
          sources={sources}
        />
      ),
    },
    {
      id: 'timeline',
      label: 'Timeline',
      content: <TimelineTab events={events} />,
    },
  ]
  if (hasLeakSiteActivity && id) {
    tabs.push({
      id: 'recent-claims',
      label: 'Recent Claims',
      content: <RecentClaimsTab actorId={id} />,
    })
  }

  return (
    <InvestigationSpine
      title={actor.name}
      contextStrip={<ActorContextStrip actor={actor} />}
      actionBar={
        <DetailActionBar
          url={typeof window !== 'undefined' ? window.location.href : ''}
          watched={watched}
          onToggleWatch={setWatched}
          entity={{ type: 'ThreatActor', name: actor.name }}
        />
      }
      tabs={tabs}
    />
  )
}
